import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import apiService from '../../../apiService';
import { fmtMoney, fmtInt, fmtDateTime, Card, Spinner, usePeriodControls } from '../financeShared';

const CATEGORY_LABELS = {
  legacy_mirror: 'Miroir legacy', peer_transfer: 'Transfert P2P', admin_adjustment: 'Ajustement admin', payout: 'Versement', system: 'Système',
};
const CAT_COLOR = {
  legacy_mirror: '#5C636E', peer_transfer: '#FF7800', admin_adjustment: '#FF9B00', payout: '#dc2626', system: '#FF5500',
};

export default function LedgerTab({ currency, rate }) {
  const { params, qs, controls } = usePeriodControls('alltime', 'month');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('');
  const [account, setAccount] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const fmt = (n) => fmtMoney(n, currency, rate);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams(qs());
      p.set('page', page);
      if (category) p.set('category', category);
      if (status) p.set('status', status);
      if (account.trim()) p.set('account', account.trim());
      if (q.trim()) p.set('q', q.trim());
      const res = await apiService.get(`/admin/finance/ledger?${p}`, { userType: 'admin' });
      if (res?.success) setData(res);
    } catch (e) { console.error('ledger:', e); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, page, category, status, account, q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [params, category, status, account, q]);

  const transfers = data?.transfers || [];
  const byCategory = data?.byCategory || [];

  return (
    <>
      {controls({ showGroupBy: false })}

      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '1.25rem' }}>
        <div style={{ position: 'relative', flex: '1', minWidth: '180px' }}>
          <Search size={15} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9AA0A8' }} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Note / ID transfert…" style={{ width: '100%', padding: '0.45rem 0.6rem 0.45rem 2rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.85rem' }} />
        </div>
        <input value={account} onChange={e => setAccount(e.target.value)} placeholder="Compte (ex. platform:…)" style={{ padding: '0.45rem 0.7rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.85rem' }} />
        <select value={category} onChange={e => setCategory(e.target.value)} style={{ padding: '0.45rem 0.7rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.85rem', background: 'white' }}>
          <option value="">Toutes catégories</option>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ padding: '0.45rem 0.7rem', borderRadius: '6px', border: '1px solid #E9EBEF', fontSize: '0.85rem', background: 'white' }}>
          <option value="">Tous statuts</option>
          <option value="posted">Posté</option>
          <option value="reversed">Annulé</option>
        </select>
      </div>

      {/* Récap par catégorie */}
      {byCategory.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' }}>
          {byCategory.map(c => (
            <div key={c.category} style={{ background: 'white', borderRadius: '10px', padding: '0.85rem 1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', borderLeft: `3px solid ${CAT_COLOR[c.category] || '#5C636E'}` }}>
              <p style={{ fontSize: '0.7rem', color: '#5C636E', margin: 0, textTransform: 'uppercase', fontWeight: 600 }}>{CATEGORY_LABELS[c.category] || c.category}</p>
              <p style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1E2229', margin: '0.2rem 0 0' }}>{fmt(c.total)}</p>
              <p style={{ fontSize: '0.7rem', color: '#9AA0A8', margin: 0 }}>{fmtInt(c.count)} transfert(s)</p>
            </div>
          ))}
        </div>
      )}

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? <div style={{ padding: '2rem' }}><Spinner /></div> : transfers.length === 0 ? <p style={{ color: '#9AA0A8', padding: '2rem' }}>Aucun transfert.</p> : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead><tr style={{ background: '#FFFFFF', borderBottom: '2px solid #E9EBEF' }}>
                {['Date', 'ID', 'De', 'Vers', 'Catégorie', 'Montant', 'Statut'].map(h => (
                  <th key={h} style={{ padding: '0.7rem 1rem', textAlign: h === 'Montant' ? 'right' : 'left', fontWeight: 700, color: '#414852', fontSize: '0.75rem', textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {transfers.map((t, idx) => (
                  <tr key={t.transfer_id} style={{ borderBottom: '1px solid #E9EBEF', background: idx % 2 === 0 ? '#fff' : '#FFFFFF' }}>
                    <td style={{ padding: '0.6rem 1rem', color: '#5C636E', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>{fmtDateTime(t.created_at)}</td>
                    <td style={{ padding: '0.6rem 1rem', color: '#1E2229', fontSize: '0.8rem', fontWeight: 600 }}>{t.public_id || '—'}</td>
                    <td style={{ padding: '0.6rem 1rem', color: '#414852', fontSize: '0.78rem', fontFamily: 'monospace' }}>{t.from_account_key}</td>
                    <td style={{ padding: '0.6rem 1rem', color: '#414852', fontSize: '0.78rem', fontFamily: 'monospace' }}>{t.to_account_key}</td>
                    <td style={{ padding: '0.6rem 1rem' }}>
                      <span style={{ fontSize: '0.72rem', padding: '0.15rem 0.5rem', borderRadius: '10px', background: '#F4F5F7', color: CAT_COLOR[t.category] || '#5C636E', fontWeight: 600 }}>{CATEGORY_LABELS[t.category] || t.category}</span>
                    </td>
                    <td style={{ padding: '0.6rem 1rem', textAlign: 'right', fontWeight: 700, color: '#1E2229', whiteSpace: 'nowrap' }}>{fmt(t.amount)}</td>
                    <td style={{ padding: '0.6rem 1rem', fontSize: '0.78rem', color: t.status === 'reversed' ? '#dc2626' : '#10B981', fontWeight: 600 }}>{t.status === 'reversed' ? 'Annulé' : 'Posté'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {data?.pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1rem', marginTop: '1.25rem' }}>
          <button className="btn-secondary" disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ padding: '0.4rem 0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}><ChevronLeft size={15} /> Préc.</button>
          <span style={{ fontSize: '0.85rem', color: '#5C636E' }}>Page {page} / {data.pages} · {fmtInt(data.total)} transferts</span>
          <button className="btn-secondary" disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} style={{ padding: '0.4rem 0.8rem', display: 'flex', alignItems: 'center', gap: 4 }}>Suiv. <ChevronRight size={15} /></button>
        </div>
      )}
    </>
  );
}
